import { Stack, Box } from '@mui/material';
import { Typography, Button } from '@sweep-io/sweep-design';
import emptyStateImage from './emptyState.png';
import { useMarketingAttributionModels } from './useMarketingAttributionModels';
import WithPermissions from '../../../../common/WithPermissions';
import { useExpandedMode } from '../../../configuration-canvas/panels-reserve-space/useExpandedMode';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../../../reducers/userInfoReducer';

const listItems: { strong: string; text: string }[] = [
  {
    strong: 'Easy Configuration:',
    text: 'Set up powerful attribution models with an intuitive interface—no custom code or lengthy Salesforce projects required.',
  },
  {
    strong: 'Centralized Data Object:',
    text: 'Access all your attribution-related data in one place, simplifying queries for in-depth marketing analysis.',
  },
  {
    strong: 'Advanced Insights & Reporting:',
    text: 'Track multiple attribution models simultaneously, providing real-time insights directly tied to revenue performance.',
  },
];

const listItemsSmallScreen: { strong: string; text: string }[] = [
  {
    strong: 'Easy Setup:',
    text: 'Launch attribution models with zero code or Salesforce projects.',
  },
  {
    strong: 'All-in-One Data:',
    text: 'Centralized object for simplified marketing analysis.',
  },
  {
    strong: 'Real-Time Insights:',
    text: 'Run multiple models and tie campaigns to revenue.',
  },
];

export const EmptyState = () => {
  const { addMarketingAttributionModelsTemplates, isLoading } = useMarketingAttributionModels();
  const { isExpandedMode } = useExpandedMode();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';

  const buttonJsx = (
    <Box>
      <WithPermissions
        accessValidation={{ type: 'query', queries: [{ permission: 'create:marketing-attributions', crmOrgId }] }}
        disabledTooltip={'To activate attribution models, please contact your admin.'}
      >
        <Button size="medium"
          variant="primary"
          loading={isLoading}
          onClick={() => addMarketingAttributionModelsTemplates()}
        >
          Activate Attribution Models
        </Button>
      </WithPermissions>
    </Box>
  );

  const maximizeYourRoiJsx = (
    <Typography variant="body">
      Maximize your marketing ROI with attribution that connects every campaign to business
      outcomes.
    </Typography>
  );

  if (isExpandedMode) {
    return (
      <Stack margin="0 auto" height="100%" p="100px 64px 0">
        <Stack
          direction="row"
          justifyContent="space-between"
          maxWidth="1456px"
          width="100%"
          alignItems="center"
          gap={12}
          height="370px"
        >
          <Stack maxWidth="630px" minWidth="350px" gap={3} flex="auto">
            <Typography variant="h1">
              Up-level your marketing insights with Sweep's Attribution Product
            </Typography>

            <Box component="ul" sx={{ paddingInlineStart: 2.5, m: 0 }}>
              {listItems.map((item, idx) => (
                <Box
                  component="li"
                  key={item.strong}
                  sx={{ mb: idx === listItems.length - 1 ? 0 : 1 }}
                >
                  <Typography variant="body-bold">
                    {item.strong} <Typography variant="body">{item.text}</Typography>
                  </Typography>
                </Box>
              ))}
            </Box>

            {maximizeYourRoiJsx}
            {buttonJsx}
          </Stack>
          <img src={emptyStateImage} alt="empty state" width="588px" height="288px" />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack margin="0 auto" height="100%" p="16px 16px 0" gap={3}>
      <Typography variant="h1">Boost Marketing ROI with Attribution</Typography>

      <Box component="ul" sx={{ paddingInlineStart: 2.5, m: 0 }}>
        {listItemsSmallScreen.map((item, idx) => (
          <Box component="li" key={item.strong} sx={{ mb: idx === listItems.length - 1 ? 0 : 1 }}>
            <Typography variant="body-bold">
              {item.strong} <Typography variant="body">{item.text}</Typography>
            </Typography>
          </Box>
        ))}
      </Box>

      {maximizeYourRoiJsx}
      {buttonJsx}
    </Stack>
  );
};
